import { useEffect, useId, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent, type ReactNode } from "react";
import { Link } from "react-router";
import { t } from "../lib/sprache";

/**
 * Diagramme mit den Bausteinen des Design-Pakets: Linien (.revenue-line + .chart-legend), Balken (.chart/.bar),
 * Ranglisten (.rank-row) und ein Ring für Anteile. Reines SVG/HTML, ohne Diagramm-Bibliothek.
 * Jede Grafik hat eine Wertetabelle als Alternative (Regel „Diagramm: Datenalternative“ in 08_komponenten.md).
 */

export interface Series {
  key: string;
  label: string;
  values: number[];
  /** Farbe als CSS-Wert, z. B. var(--tk-s1) */
  color: string;
  dashed?: boolean;
  fill?: boolean;
}

/** Breite eines Elements in Pixeln (für Diagramme, die in echten Pixeln zeichnen und so überall lesbar bleiben). */
function useWidth<T extends HTMLElement>(fallback: number) {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(fallback);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver((entries) => {
      const w = Math.round(entries[0]?.contentRect.width ?? fallback);
      if (w > 0) setWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [fallback]);
  return { ref, width };
}

/**
 * Achse mit runden Rasterwerten (SICHT-AGENTUR-REST-29): Schritt 1, 2, 2,5 oder 5 × 10^k, drei bis fünf Abschnitte.
 * Vorher teilte das Raster den gerundeten Höchstwert in Viertel („3,8k“, „11,3k“).
 * ganzzahlig (Zählwerte, Traffic /betrieb): nur ganze Schritte ab 1, sonst stünden bei kleinen Werten doppelte gerundete
 * Beschriftungen an der Achse („0, 0, 1, 1, 1“).
 */
export function achsenSkala(v: number, ganzzahlig = false): { max: number; ticks: number[] } {
  const top = v > 0 ? v : 1;
  const roh = top / 4;
  const exp = Math.pow(10, Math.floor(Math.log10(roh)));
  let step = 10 * exp;
  for (const f of [1, 2, 2.5, 5, 10]) {
    const s = f * exp;
    if (ganzzahlig && (s < 1 || !Number.isInteger(s))) continue;
    if (s >= roh) { step = s; break; }
  }
  if (ganzzahlig) step = Math.max(1, Math.round(step));
  const teile = Math.max(1, Math.ceil(top / step - 1e-9));
  return { max: step * teile, ticks: Array.from({ length: teile + 1 }, (_, i) => step * i) };
}
/**
 * Beschriftete Monate (SICHT-AGENTUR-REST-29): jeder `every`-te, dazu der letzte und der gewählte; ein regulärer Monat
 * zu nah an diesen beiden entfällt, sonst stoßen die Wörter aneinander („AugSep“).
 */
export function achsenLabels(n: number, every: number, current: number | null | undefined): Set<number> {
  const fest = [n - 1, current].filter((i): i is number => typeof i === "number" && i >= 0 && i < n);
  const out = new Set<number>(fest);
  for (let i = 0; i < n; i += every) if (fest.every((f) => f === i || Math.abs(f - i) >= every)) out.add(i);
  return out;
}

export function ValueTable({ caption, columns, rows }: { caption: string; columns: string[]; rows: (string | number)[][] }) {
  return (
    <details className="tk-values">
      <summary>{t("Werte als Tabelle", "Values as table")}</summary>
      {/* N-4: ab vier Spalten am Handy Karten (je Zeile eine Karte, Spaltenname vor dem Wert); schmale Tabellen bleiben Tabellen */}
      <div className={columns.length > 3 ? "tablewrap tk-cardtable tk-handykarten" : "tablewrap"}>
        <table>
          <caption className="sr-only">{caption}</caption>
          <thead>
            <tr>{columns.map((c, i) => <th key={c} scope="col" className={i > 0 ? "money" : undefined}>{c}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>{r.map((c, j) => j > 0
                ? <td key={j} data-label={columns[j]}><span className="num">{c}</span></td>
                : <td key={j} className="tk-karte-titel">{c}</td>)}</tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  );
}

/**
 * Liniendiagramm mit mehreren Reihen. Klick in der Legende hebt eine Reihe hervor oder blendet sie zurück
 * (wie im bisherigen Portal: Linien bleiben sichtbar, nur die Betonung ändert sich). Monate sind fokussierbar;
 * Klick oder Enter öffnet die Monatsdetails.
 */
export function LineChart({
  labels, series, format, short, initialActive, onPick, caption, height = 280, current, compact, hideInactive, labelColumn, emptyText, ganzzahlig,
}: {
  labels: string[];
  series: Series[];
  format: (v: number) => string;
  short: (v: number) => string;
  initialActive?: string[];
  onPick?: (index: number) => void;
  caption: string;
  height?: number;
  /** Index des laufenden Monats (fett beschriftet) */
  current?: number;
  /** Für halbe Breite: schmalere Zeichenfläche, damit Beschriftungen lesbar bleiben */
  compact?: boolean;
  /** Legende schaltet Linien ganz aus statt sie nur abzuschwächen (Traffic-Dashboard, 0330); mindestens eine bleibt an */
  hideInactive?: boolean;
  /** Spaltenkopf der Wertetabelle (Standard „Monat“) */
  labelColumn?: string;
  /** Hinweis, wenn alle gezeigten Werte null sind */
  emptyText?: string;
  /** Zählwerte: nur ganzzahlige, eindeutige Rasterwerte an der y-Achse */
  ganzzahlig?: boolean;
}) {
  const [active, setActive] = useState<Set<string>>(() => new Set(initialActive ?? series.map((s) => s.key)));
  const [hover, setHover] = useState<number | null>(null);
  const gradId = useId().replace(/:/g, "");
  const { ref: plotRef, width } = useWidth<HTMLDivElement>(compact ? 480 : 930);
  const narrow = width < 520;
  const W = Math.max(280, width), H = narrow ? Math.min(height, 220) : height, top = 24, bottom = H - 42, left = narrow || compact ? 44 : 64, right = W - 16;
  const shown = series.filter((s) => active.has(s.key));
  const skala = achsenSkala(Math.max(1, ...shown.flatMap((s) => s.values)), ganzzahlig);
  const max = skala.max;
  const n = labels.length;
  const x = (i: number) => (n <= 1 ? (left + right) / 2 : left + (i * (right - left)) / (n - 1));
  const y = (v: number) => bottom - (Math.max(0, v) / max) * (bottom - top);
  const grid = skala.ticks.map((v) => v / max);
  const toggle = (k: string) => setActive((a) => {
    const next = new Set(a);
    if (next.has(k)) { if (next.size > 1) next.delete(k); } else next.add(k);
    return next;
  });
  // Tippen und Wischen am Handy zeigt den Tageswert (0330): Stelle aus der Fingerposition, waagerechtes Wischen wandert mit
  const indexAt = (svg: SVGSVGElement, clientX: number) => {
    const box = svg.getBoundingClientRect();
    const px = box.width > 0 ? ((clientX - box.left) / box.width) * W : 0;
    return n <= 1 ? 0 : Math.max(0, Math.min(n - 1, Math.round(((px - left) / (right - left)) * (n - 1))));
  };
  // Nach einer Berührung bleibt der Hinweis stehen: nachgeschobene Maus- und Fokus-Ereignisse des Browsers schließen ihn nicht
  const beruehrt = useRef(false);
  const touch = (e: ReactPointerEvent<SVGSVGElement>) => {
    beruehrt.current = e.pointerType !== "mouse";
    if (beruehrt.current) setHover(indexAt(e.currentTarget, e.clientX));
  };
  const schliessen = () => { if (!beruehrt.current) setHover(null); };
  const leer = !!emptyText && shown.every((s) => s.values.every((v) => !v));
  const band = n > 1 ? (right - left) / (n - 1) : right - left;
  const labelEvery = band < 40 ? Math.ceil(40 / band) : 1;
  const beschriftet = achsenLabels(n, labelEvery, current);
  return (
    <div className="tk-chart">
      <div className="chart-legend" role="group" aria-label={t("Linien hervorheben", "Highlight lines")}>
        {series.map((s) => (
          <button key={s.key} type="button" className="tk-legend" aria-pressed={active.has(s.key)} onClick={() => toggle(s.key)}>
            <i style={active.has(s.key) ? { background: s.color } : { background: "transparent", boxShadow: `inset 0 0 0 1.5px ${s.color}` }} />
            {s.label}
          </button>
        ))}
      </div>
      <div className="tk-chart-plot" ref={plotRef}>
        <svg className="revenue-line" viewBox={`0 0 ${W} ${H}`} width={W} height={H} style={{ width: "100%", height: `${H}px`, touchAction: "pan-y" }} role="img" aria-label={caption} onMouseLeave={schliessen}
          onPointerDown={touch} onPointerMove={(e) => { if (e.pointerType === "mouse") beruehrt.current = false; else if (e.buttons) touch(e); }}>
          <defs>
            {series.filter((s) => s.fill).map((s) => (
              <linearGradient key={s.key} id={`${gradId}-${s.key}`} x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor={s.color} stopOpacity="0.16" />
                <stop offset="100%" stopColor={s.color} stopOpacity="0" />
              </linearGradient>
            ))}
          </defs>
          {grid.map((g) => (
            <g key={g}>
              <line className="gridline" x1={left - 8} y1={y(max * g)} x2={right} y2={y(max * g)} />
              <text x="0" y={y(max * g) + 4}>{short(max * g)}</text>
            </g>
          ))}
          {series.map((s) => {
            const on = active.has(s.key);
            if (hideInactive && !on) return null;
            const pts = s.values.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(" ");
            return (
              <g key={s.key} opacity={on ? 1 : 0.16}>
                {s.fill && on && <polygon points={`${x(0)},${bottom} ${pts} ${x(n - 1)},${bottom}`} fill={`url(#${gradId}-${s.key})`} />}
                <polyline points={pts} fill="none" stroke={s.color} strokeWidth={on ? 2.5 : 1.5} strokeDasharray={s.dashed ? "6 5" : undefined} strokeLinejoin="round" strokeLinecap="round" />
                {on && s.values.map((v, i) => <circle key={i} cx={x(i)} cy={y(v)} r={hover === i ? 5 : 3.2} fill={s.color} />)}
              </g>
            );
          })}
          {hover !== null && <line x1={x(hover)} x2={x(hover)} y1={top} y2={bottom} stroke="var(--line)" strokeWidth="1" />}
          {labels.map((l, i) => beschriftet.has(i) && (
            <text key={i} x={x(i)} y={H - 16} textAnchor="middle" fontWeight={current === i ? 700 : 400}>{l}</text>
          ))}
          {labels.map((l, i) => (
            <rect
              key={`hit-${i}`} x={x(i) - band / 2} y={top - 10} width={band} height={bottom - top + 20} fill="transparent"
              tabIndex={onPick ? 0 : -1} role={onPick ? "button" : undefined}
              aria-label={`${l}: ${shown.map((s) => `${s.label} ${format(s.values[i] ?? 0)}`).join(", ")}`}
              style={{ cursor: onPick ? "pointer" : "default", outline: "none" }}
              onMouseEnter={() => { if (!beruehrt.current) setHover(i); }} onFocus={() => { if (!beruehrt.current) setHover(i); }} onBlur={schliessen}
              onClick={() => onPick?.(i)} onKeyDown={(e) => { if (onPick && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); onPick(i); } }}
            />
          ))}
        </svg>
        {hover !== null && (
          // Am Rand bleibt der Hinweis in der Karte (am Handy sonst halb außerhalb)
          <div className="tk-tip" style={{ left: `min(max(${((x(hover) / W) * 100).toFixed(2)}%, 112px), calc(100% - 112px))` }} role="presentation">
            <strong>{labels[hover]}</strong>
            {shown.map((s) => (
              <span key={s.key}><i style={{ background: s.color }} />{s.label}<b className="num">{format(s.values[hover] ?? 0)}</b></span>
            ))}
            {onPick && <small>{t("Klicken für Details", "Click for details")}</small>}
          </div>
        )}
      </div>
      {leer && <p className="muted tk-chart-leer">{emptyText}</p>}
      <ValueTable caption={caption} columns={[labelColumn ?? t("Monat", "Month"), ...series.map((s) => s.label)]} rows={labels.map((l, i) => [l, ...series.map((s) => format(s.values[i] ?? 0))])} />
    </div>
  );
}

/**
 * Gestapelte Säulen je Tag (Reichweite der Betreiber-Konsole, Inhaber 24.10.2026): eine Säule je Beschriftung, darin die Reihen
 * übereinander. Die Legende blendet Reihen aus (mindestens eine bleibt), Zeigen oder Tippen zeigt die Werte des Tages.
 * Achse und Beschriftungen wie LineChart; Wertetabelle als Alternative.
 */
export function StackedBars({ labels, series, format, short, caption, height = 280, labelColumn, emptyText }: {
  labels: string[];
  series: Series[];
  format: (v: number) => string;
  short: (v: number) => string;
  caption: string;
  height?: number;
  labelColumn?: string;
  emptyText?: string;
}) {
  const [active, setActive] = useState<Set<string>>(() => new Set(series.map((s) => s.key)));
  const [hover, setHover] = useState<number | null>(null);
  const musterId = useId().replace(/:/g, "");
  const { ref: plotRef, width } = useWidth<HTMLDivElement>(930);
  const narrow = width < 520;
  const W = Math.max(280, width), H = narrow ? Math.min(height, 220) : height, top = 16, bottom = H - 34, left = narrow ? 40 : 56, right = W - 8;
  const shown = series.filter((s) => active.has(s.key));
  const n = labels.length;
  const summen = labels.map((_, i) => shown.reduce((a, s) => a + Math.max(0, s.values[i] ?? 0), 0));
  const skala = achsenSkala(Math.max(1, ...summen), true);
  const y = (v: number) => bottom - (v / skala.max) * (bottom - top);
  const band = n > 0 ? (right - left) / n : right - left;
  const breite = Math.max(2, Math.min(28, band * 0.64));
  const xm = (i: number) => left + band * i + band / 2;
  const labelEvery = band < 40 ? Math.ceil(40 / band) : 1;
  const beschriftet = achsenLabels(n, labelEvery, null);
  const toggle = (k: string) => setActive((a) => {
    const next = new Set(a);
    if (next.has(k)) { if (next.size > 1) next.delete(k); } else next.add(k);
    return next;
  });
  const indexAt = (svg: SVGSVGElement, clientX: number) => {
    const box = svg.getBoundingClientRect();
    const px = box.width > 0 ? ((clientX - box.left) / box.width) * W : 0;
    return Math.max(0, Math.min(n - 1, Math.floor((px - left) / band)));
  };
  const beruehrt = useRef(false);
  const touch = (e: ReactPointerEvent<SVGSVGElement>) => {
    beruehrt.current = e.pointerType !== "mouse";
    if (beruehrt.current) setHover(indexAt(e.currentTarget, e.clientX));
  };
  const leer = !!emptyText && summen.every((v) => !v);
  return (
    <div className="tk-chart tk-stapel">
      <div className="chart-legend" role="group" aria-label={t("Reihen ein- und ausblenden", "Show or hide series")}>
        {series.map((s) => (
          <button key={s.key} type="button" className="tk-legend" aria-pressed={active.has(s.key)} onClick={() => toggle(s.key)}>
            <i style={active.has(s.key)
              ? (s.dashed ? { background: `repeating-linear-gradient(45deg, ${s.color} 0 2px, transparent 2px 4px)`, boxShadow: `inset 0 0 0 1px ${s.color}` } : { background: s.color })
              : { background: "transparent", boxShadow: `inset 0 0 0 1.5px ${s.color}` }} />
            {s.label}
          </button>
        ))}
      </div>
      <div className="tk-chart-plot" ref={plotRef}>
        <svg className="revenue-line" viewBox={`0 0 ${W} ${H}`} width={W} height={H} style={{ width: "100%", height: `${H}px`, touchAction: "pan-y" }} role="img" aria-label={caption}
          onMouseLeave={() => { if (!beruehrt.current) setHover(null); }}
          onPointerDown={touch} onPointerMove={(e) => { if (e.pointerType === "mouse") { beruehrt.current = false; setHover(indexAt(e.currentTarget, e.clientX)); } else if (e.buttons) touch(e); }}>
          {/* Gestrichelte Reihen als Schraffur (Sonstige neben gleich grauen Reihen unterscheidbar) */}
          <defs>
            {series.filter((s) => s.dashed).map((s) => (
              <pattern key={s.key} id={`${musterId}-${s.key}`} width="5" height="5" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                <rect width="5" height="5" fill="transparent" /><line x1="0" y1="0" x2="0" y2="5" stroke={s.color} strokeWidth="2.5" />
              </pattern>
            ))}
          </defs>
          {skala.ticks.map((v) => (
            <g key={v}>
              <line className="gridline" x1={left - 8} y1={y(v)} x2={right} y2={y(v)} />
              <text x="0" y={y(v) + 4}>{short(v)}</text>
            </g>
          ))}
          {hover !== null && <rect x={left + band * hover} y={top} width={band} height={bottom - top} fill="var(--line)" />}
          {labels.map((_, i) => {
            let unten = 0;
            return (
              <g key={i}>
                {shown.map((s) => {
                  const v = Math.max(0, s.values[i] ?? 0);
                  if (!v) return null;
                  const y0 = y(unten), y1 = y(unten + v);
                  unten += v;
                  return <rect key={s.key} x={xm(i) - breite / 2} y={y1} width={breite} height={Math.max(1, y0 - y1 - 1)} rx={Math.min(3, breite / 4)}
                    fill={s.dashed ? `url(#${musterId}-${s.key})` : s.color} stroke={s.dashed ? s.color : undefined} strokeWidth={s.dashed ? 1 : undefined} />;
                })}
              </g>
            );
          })}
          {/* Letzte Beschriftung bündig rechts, sonst ragt sie am Handy aus der Zeichenfläche */}
          {labels.map((l, i) => beschriftet.has(i) && (
            <text key={`l-${i}`} x={i === n - 1 ? right : xm(i)} y={H - 10} textAnchor={i === n - 1 ? "end" : "middle"}>{l}</text>
          ))}
        </svg>
        {hover !== null && (
          <div className="tk-tip" style={{ left: `min(max(${((xm(hover) / W) * 100).toFixed(2)}%, 112px), calc(100% - 112px))` }} role="presentation">
            <strong>{labels[hover]} · {format(summen[hover] ?? 0)}</strong>
            {shown.filter((s) => (s.values[hover] ?? 0) > 0).map((s) => (
              <span key={s.key}><i style={{ background: s.color }} />{s.label}<b className="num">{format(s.values[hover] ?? 0)}</b></span>
            ))}
          </div>
        )}
      </div>
      {leer && <p className="muted tk-chart-leer">{emptyText}</p>}
      <ValueTable caption={caption} columns={[labelColumn ?? t("Tag", "Day"), ...series.map((s) => s.label), t("Zusammen", "Total")]}
        rows={labels.map((l, i) => [l, ...series.map((s) => format(s.values[i] ?? 0)), format(series.reduce((a, s) => a + (s.values[i] ?? 0), 0))])} />
    </div>
  );
}

/** Säulen (.chart/.bar aus dem Paket) mit Wert über der Säule. */
export function BarChart({ items, format, caption, tones }: {
  items: { label: string; value: number; hint?: string }[];
  format: (v: number) => string;
  caption: string;
  tones?: ("neutral" | "info" | "warning" | "error" | "success")[];
}) {
  const max = Math.max(1, ...items.map((i) => i.value));
  return (
    // Ab sieben Säulen blendet app.css in schmalen Karten die Werte über den Säulen und jede zweite Beschriftung aus (SICHT-OEFFENTLICH-BETRIEB-26)
    <div className={`tk-barchart${items.length > 6 ? " tk-bars-many" : ""}`}>
      <div role="img" aria-label={`${caption}: ${items.map((i) => `${i.label} ${format(i.value)}`).join(", ")}`}>
        <div className="chart">
          {items.map((it, i) => (
            <div key={it.label} className={`bar${tones?.[i] ? ` tk-bar-${tones[i]}` : ""}`} style={{ height: `${Math.max(4, (it.value / max) * 100)}%` }} title={it.hint}>
              <span>{format(it.value)}</span>
            </div>
          ))}
        </div>
        <div className="chartlabels">
          {items.map((it) => {
            const [name, ...rest] = it.label.split(" · ");
            return <span key={it.label}>{name}{rest.length > 0 && <small>{rest.join(" · ")}</small>}</span>;
          })}
        </div>
      </div>
      <ValueTable caption={caption} columns={["", t("Wert", "Value")]} rows={items.map((i) => [i.label, format(i.value)])} />
    </div>
  );
}

/** Rangliste mit Balken (.rank-row aus dem Paket). */
export function RankBars({ rows, format, empty }: {
  rows: { key: string; label: string; to?: string; value: number; note?: ReactNode }[];
  format: (v: number) => string;
  empty: string;
}) {
  const max = Math.max(1, ...rows.map((r) => Math.abs(r.value)));
  if (!rows.length) return <p className="muted">{empty}</p>;
  return (
    <div>
      {rows.map((r) => (
        <div key={r.key} className="rank-row">
          <span className="tk-rank-label">{r.to ? <Link to={r.to}>{r.label}</Link> : r.label}</span>
          <div className="rank-track"><div className="rank-fill" style={{ width: `${(Math.abs(r.value) / max) * 100}%` }} /></div>
          <strong className="num">{format(r.value)}{r.note}</strong>
        </div>
      ))}
    </div>
  );
}

/** Ring für Anteile (höchstens fünf Teile, Rest als „Weitere“), mit Legende und Prozenten. */
export function Donut({ segments, format, caption, colors }: {
  segments: { label: string; value: number }[];
  format: (v: number) => string;
  caption: string;
  colors: string[];
}) {
  const parts = useMemo(() => {
    const sorted = [...segments].filter((s) => s.value > 0).sort((a, b) => b.value - a.value);
    if (sorted.length <= 5) return sorted;
    const rest = sorted.slice(4).reduce((a, s) => a + s.value, 0);
    return [...sorted.slice(0, 4), { label: t("Weitere", "Other"), value: rest }];
  }, [segments]);
  const total = parts.reduce((a, s) => a + s.value, 0);
  const R = 52, C = 2 * Math.PI * R;
  let offset = 0;
  const ref = useRef<SVGSVGElement>(null);
  if (!total) return <p className="muted">{t("Im gewählten Zeitraum gibt es keine Umsätze.", "There is no revenue in the selected period.")}</p>;
  return (
    <div className="tk-donut">
      <svg ref={ref} viewBox="0 0 140 140" role="img" aria-label={`${caption}: ${parts.map((p) => `${p.label} ${Math.round((p.value / total) * 100)} %`).join(", ")}`}>
        <circle cx="70" cy="70" r={R} fill="none" stroke="var(--subtle)" strokeWidth="18" />
        {parts.map((p, i) => {
          const len = (p.value / total) * C;
          const el = <circle key={p.label} cx="70" cy="70" r={R} fill="none" stroke={colors[i % colors.length]} strokeWidth="18"
            strokeDasharray={`${len} ${C - len}`} strokeDashoffset={-offset} transform="rotate(-90 70 70)" />;
          offset += len;
          return el;
        })}
        <text x="70" y="66" textAnchor="middle" className="tk-donut-total">{format(total)}</text>
        <text x="70" y="84" textAnchor="middle" className="tk-donut-sub">{t("gesamt", "total")}</text>
      </svg>
      <ul className="tk-donut-legend">
        {parts.map((p, i) => (
          <li key={p.label}>
            <i style={{ background: colors[i % colors.length] }} />
            <span>{p.label}</span>
            <strong className="num">{format(p.value)}</strong>
            <small>{Math.round((p.value / total) * 100)} %</small>
          </li>
        ))}
      </ul>
    </div>
  );
}
